import React, { createContext, useContext, useEffect, useState } from 'react'
import { paths } from '@educator/utils/paths'
import type { EducatorRole } from '@mc/educator-api'
import { UserAnalytics } from '@mc/educator-api'
import { useTranslation } from '@mc/i18n'
import { primeAccountProperties } from '@mc/user-info'
import { useGlobalErrorModal } from '../components/GlobalErrorModal'
import { getBasePath } from '../config/basePath'
import { forceLogout } from '../utils/logout'

export interface EducatorUser {
  sub: string
  name?: string
  firstName?: string
  lastName?: string
  email?: string
  picture?: string
  nickname?: string
  updated_at?: string
  analytics?: UserAnalytics
  // Server-authoritative educator role from /api/auth/me (sourced from the
  // backend /api/v1/educator/auth/users/me endpoint). Typed with the spec's
  // `EducatorRole` union (`learner` | `beta` | `preview`) — see the educator
  // API contract in api/educator. The source of truth for gating the MCX beta
  // experience; consumers read `role` directly. See JAR-2376 / JAR-2377 /
  // JAR-2406.
  role?: EducatorRole
}

interface AuthContextValue {
  user: EducatorUser | undefined
  error: Error | undefined
  isLoading: boolean
}

const AuthContext = createContext<AuthContextValue>({
  user: undefined,
  error: undefined,
  isLoading: true,
})

export const useUser = () => useContext(AuthContext)

const getPathWithoutBasePath = (pathname: string, basePath: string) => {
  if (!basePath || basePath === '/' || !pathname.startsWith(basePath)) {
    return pathname
  }

  const normalizedPath = pathname.slice(basePath.length)
  return normalizedPath.length ? normalizedPath : '/'
}

const shouldRedirectToLogin = (pathname: string, basePath: string) => {
  const path = getPathWithoutBasePath(pathname, basePath)

  return (
    path !== paths.root &&
    !path.includes(paths.login) &&
    !path.includes(paths.email) &&
    !path.includes(paths.register) &&
    !path.includes(paths.resetPassword) &&
    !path.includes(paths.programDetails) &&
    !path.includes(paths.postLogin) &&
    !path.includes(paths.subjects) &&
    !path.includes(paths.enrollmentWaitlist) &&
    // Internal paths for testing
    !path.includes(paths.playground) &&
    path !== paths.autoCheckout
  )
}

// A transient upstream blip — a backend hiccup, a refresh-token rotation
// straggler (see JAR-2156 / the Redis-coordinated exchange in tokenRefresh.ts),
// or a network error — can make /api/auth/me fail even though the user is still
// authenticated. Without a retry that surfaces on refresh as either the
// "invalid account" modal (403) or a forced logout (5xx / network). Retry with
// exponential backoff + jitter so a retry lands after the upstream OAuth
// exchange settles (commonly a few hundred ms) and rides out a short backend
// blip — not just a sub-300ms flicker. No session is a 200 with a null body,
// so it is never retried. See JAR-2288.
const ME_MAX_RETRIES = 2 // up to 3 attempts total
const ME_RETRY_BASE_DELAY_MS = 300

const delay = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms)
  })

const isRetryableMeFailure = (status: number) => status === 403 || status >= 500

// Exponential backoff (~300ms, ~900ms) with up to +30% jitter so concurrent
// requests and tabs don't retry in lockstep and re-collide.
const getRetryDelayMs = (attempt: number) => {
  const base = ME_RETRY_BASE_DELAY_MS * 3 ** attempt
  return base + Math.random() * base * 0.3
}

const fetchMeWithRetry = async (url: string): Promise<Response> => {
  // The most recent retryable Response, if any. If a later attempt throws
  // (network error) we return this rather than letting the throw propagate, so
  // the caller keeps its status-based branching (e.g. the 403 invalid-account
  // modal) instead of falling through to the generic error/logout path.
  let lastRetryableResponse: Response | undefined

  for (let attempt = 0; attempt <= ME_MAX_RETRIES; attempt += 1) {
    const isLastAttempt = attempt === ME_MAX_RETRIES
    try {
      // eslint-disable-next-line no-await-in-loop
      const response = await fetch(url)
      if (response.ok || !isRetryableMeFailure(response.status)) {
        return response
      }
      lastRetryableResponse = response
      if (isLastAttempt) {
        return response
      }
    } catch (error) {
      // Network error — give up only once retries are exhausted, and even then
      // prefer an earlier retryable Response so status branching is preserved.
      if (isLastAttempt) {
        if (lastRetryableResponse) {
          return lastRetryableResponse
        }
        throw error
      }
    }
    // eslint-disable-next-line no-await-in-loop
    await delay(getRetryDelayMs(attempt))
  }

  // Unreachable — the final attempt always returns or throws above.
  throw new Error('fetchMeWithRetry exhausted retries')
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const { t } = useTranslation('@mc/educator')
  const { openModal } = useGlobalErrorModal()
  const [user, setUser] = useState<EducatorUser | undefined>(undefined)
  const [error, setError] = useState<Error | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(true)

  // Note: basePath is configured via the EDUCATOR_BASE_PATH env var
  // TODO: Rename EDUCATOR_BASE_PATH to EDUCATOR_BASE_PATH for consistency
  // All paths (including API routes) must include the basePath prefix
  const basePath = getBasePath()

  useEffect(() => {
    const fetchUser = async () => {
      const showInvalidAccountModal = () => {
        openModal({
          title: t('globalError.invalidAccountTitle'),
          description: t('globalError.invalidAccountDescription'),
          primaryActionLabel: t('globalError.invalidAccountCta'),
          onPrimaryAction: () => {
            forceLogout()
          },
        })
      }

      try {
        const response = await fetchMeWithRetry(`${basePath}/api/auth/me`)

        if (response.ok) {
          const userData: EducatorUser | null = await response.json()
          setUser(userData ?? undefined)
          if (!userData) {
            // Prime only on a confirmed no-session: the jsonapi proxy uses this same token, so its lookups can only 401
            primeAccountProperties({})
            if (shouldRedirectToLogin(window.location.pathname, basePath)) {
              await forceLogout()
            }
          }
        } else if (response.status === 403) {
          setUser(undefined)
          showInvalidAccountModal()
        } else {
          setUser(undefined)
          if (shouldRedirectToLogin(window.location.pathname, basePath)) {
            await forceLogout()
          }
        }
      } catch (err) {
        setError(err as Error)
        setUser(undefined)
        if (shouldRedirectToLogin(window.location.pathname, basePath)) {
          await forceLogout()
        }
      } finally {
        setIsLoading(false)
      }
    }

    fetchUser()
  }, [basePath, openModal, t])

  return (
    <AuthContext.Provider value={{ user, error, isLoading }}>
      {children}
    </AuthContext.Provider>
  )
}
